import { useState } from 'react';
import { clsx } from 'clsx';
import type { CharacterClass, Subclass } from '@character-creator/types';
import { useRulesetClasses } from '../hooks/useRulesetData';
import { LinkedText } from '../components/LinkedText';

const CLASS_COLORS: Record<string, string> = {
  barbarian: 'bg-class-barbarian',
  bard: 'bg-class-bard',
  cleric: 'bg-class-cleric',
  druid: 'bg-class-druid',
  fighter: 'bg-class-fighter',
  monk: 'bg-class-monk',
  paladin: 'bg-class-paladin',
  ranger: 'bg-class-ranger',
  rogue: 'bg-class-rogue',
  sorcerer: 'bg-class-sorcerer',
  warlock: 'bg-class-warlock',
  wizard: 'bg-class-wizard',
};

export function BrowseClassesPage() {
  const [selectedClass, setSelectedClass] = useState<CharacterClass | null>(null);
  const [selectedSubclass, setSelectedSubclass] = useState<Subclass | null>(null);
  const classes = useRulesetClasses();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-text-primary mb-2">
          Character Classes
        </h1>
        <p className="text-text-secondary">
          Choose from {classes.length} classes available
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Class List */}
        <div className="lg:col-span-1 space-y-3">
          {classes.map((cls) => (
            <button
              id={cls.id}
              key={cls.id}
              onClick={() => {
                setSelectedClass(cls);
                setSelectedSubclass(null);
              }}
              className={clsx(
                'w-full text-left card card-hover p-4 transition-all',
                selectedClass?.id === cls.id && 'ring-2 ring-accent-gold'
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={clsx(
                    'w-3 h-3 rounded-full',
                    CLASS_COLORS[cls.id] || 'bg-text-muted'
                  )}
                />
                <div>
                  <h3 className="font-display font-semibold text-text-primary">
                    {cls.name}
                  </h3>
                  <p className="text-sm text-text-muted">
                    Hit Die: {cls.hitDie} &bull;{' '}
                    {cls.spellcasting ? 'Spellcaster' : 'Martial'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Class Details */}
        <div className="lg:col-span-2">
          {selectedClass ? (
            <ClassDetails
              cls={selectedClass}
              selectedSubclass={selectedSubclass}
              onSelectSubclass={setSelectedSubclass}
            />
          ) : (
            <div className="card p-8 text-center text-text-muted">
              <p>Select a class to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClassDetails({
  cls,
  selectedSubclass,
  onSelectSubclass,
}: {
  cls: CharacterClass;
  selectedSubclass: Subclass | null;
  onSelectSubclass: (subclass: Subclass | null) => void;
}) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const displayedFeatures = showAllFeatures
    ? cls.features
    : cls.features.filter((f) => f.level <= 3);

  return (
    <div className="card p-6 space-y-6 animate-fade-in">
      <div className="flex items-start gap-4">
        <div
          className={clsx(
            'w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl',
            CLASS_COLORS[cls.id] || 'bg-text-muted'
          )}
        >
          {cls.name[0]}
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary">
            {cls.name}
          </h2>
          <p className="text-text-secondary mt-1">
            <LinkedText excludePages={['classes']}>{cls.description}</LinkedText>
          </p>
        </div>
      </div>

      {/* Core Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBlock label="Hit Die" value={cls.hitDie} />
        <StatBlock
          label="Primary Ability"
          value={cls.primaryAbility
            .map((a) => a.slice(0, 3).toUpperCase())
            .join(' or ')}
        />
        <StatBlock
          label="Saving Throws"
          value={cls.savingThrows
            .map((s) => s.slice(0, 3).toUpperCase())
            .join(', ')}
        />
        <StatBlock
          label={cls.subclassName}
          value={`Level ${cls.subclassLevel}`}
        />
      </div>

      {/* Proficiencies */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-3">
          Proficiencies
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-3 bg-background-secondary rounded-lg">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-1">
              Armor
            </div>
            <div className="text-text-primary">
              {cls.armorProficiencies.length > 0
                ? cls.armorProficiencies.join(', ')
                : 'None'}
            </div>
          </div>
          <div className="p-3 bg-background-secondary rounded-lg">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-1">
              Weapons
            </div>
            <div className="text-text-primary">
              {cls.weaponProficiencies.join(', ')}
            </div>
          </div>
          <div className="p-3 bg-background-secondary rounded-lg">
            <div className="text-xs text-text-muted uppercase tracking-wide mb-1">
              Skills (Choose {cls.skillChoices.count})
            </div>
            <div className="text-text-primary text-sm">
              {cls.skillChoices.from.join(', ')}
            </div>
          </div>
          {cls.toolProficiencies.length > 0 && (
            <div className="p-3 bg-background-secondary rounded-lg">
              <div className="text-xs text-text-muted uppercase tracking-wide mb-1">
                Tools
              </div>
              <div className="text-text-primary">
                {cls.toolProficiencies.join(', ')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spellcasting */}
      {cls.spellcasting && (
        <div>
          <h3 className="font-display font-semibold text-text-primary mb-3">
            Spellcasting
          </h3>
          <div className="p-4 bg-background-secondary rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-text-muted text-sm">Ability:</span>
                <span className="ml-2 text-text-primary capitalize">
                  {cls.spellcasting.ability}
                </span>
              </div>
              <div>
                <span className="text-text-muted text-sm">Type:</span>
                <span className="ml-2 text-text-primary capitalize">
                  {cls.spellcasting.type} Caster
                </span>
              </div>
              {cls.spellcasting.ritual && (
                <div>
                  <span className="text-text-muted text-sm">Ritual Casting:</span>
                  <span className="ml-2 text-success">Yes</span>
                </div>
              )}
              {cls.spellcasting.focus && (
                <div>
                  <span className="text-text-muted text-sm">Focus:</span>
                  <span className="ml-2 text-text-primary capitalize">
                    {cls.spellcasting.focus}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subclasses */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-3">
          {cls.subclassName}s
        </h3>
        <div className="flex flex-wrap gap-2">
          {cls.subclasses.map((subclass) => (
            <button
              key={subclass.id}
              onClick={() =>
                onSelectSubclass(
                  selectedSubclass?.id === subclass.id ? null : subclass
                )
              }
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                selectedSubclass?.id === subclass.id
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-elevated text-text-secondary hover:text-text-primary'
              )}
            >
              {subclass.name}
            </button>
          ))}
        </div>
        {selectedSubclass && (
          <div className="mt-4 p-4 bg-background-secondary rounded-lg animate-fade-in">
            <h4 className="font-medium text-text-primary mb-2">
              {selectedSubclass.name}
            </h4>
            <p className="text-sm text-text-secondary">
              <LinkedText excludePages={['classes']}>{selectedSubclass.description}</LinkedText>
            </p>
          </div>
        )}
      </div>

      {/* Features */}
      <div>
        <h3 className="font-display font-semibold text-text-primary mb-3">
          Class Features
        </h3>
        <div className="space-y-3">
          {displayedFeatures.map((feature) => (
            <div
              key={feature.id}
              className="p-3 bg-background-secondary rounded-lg"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-accent-gold bg-accent-gold/10 px-2 py-0.5 rounded">
                  Lvl {feature.level}
                </span>
                <h4 className="font-medium text-text-primary">{feature.name}</h4>
              </div>
              <p className="text-sm text-text-secondary mt-2">
                <LinkedText excludePages={['classes']}>{feature.description}</LinkedText>
              </p>
            </div>
          ))}
        </div>
        {cls.features.length > 3 && (
          <button
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="mt-4 text-accent-gold text-sm hover:underline"
          >
            {showAllFeatures
              ? 'Show fewer features'
              : `Show all ${cls.features.length} features`}
          </button>
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 bg-background-secondary rounded-lg">
      <div className="text-xs text-text-muted uppercase tracking-wide">
        {label}
      </div>
      <div className="font-medium text-text-primary">{value}</div>
    </div>
  );
}
